import { Editor } from '@tldraw/tldraw'
import { Action } from '../Types'
import { useRootStore } from 'stores/RootStore'
import { SharedStyleMap } from '../SharedStyleMap'
import { FontFamilyStyle, FontWeightStyle, ItalicStyle, UnderlineStyle } from '../StyleProps'
import { getActiveProseView } from '../../Shapes/TextBoxShape/activeProseView'

/**
 * A centralised set of common actions that can be triggered for text shapes in the visual editor.
 */
export const useTextActions = (editor?: Editor) => {
  const { fonts } = useRootStore()

  if (!editor) return []

  const styles = new SharedStyleMap(editor.getSharedStyles())
  const fontFamilyStyle = styles.get(FontFamilyStyle)
  const fontWeightStyle = styles.get(FontWeightStyle)
  const italicStyle = styles.get(ItalicStyle)
  const underlineStyle = styles.get(UnderlineStyle)

  const isBoldDisabled =
    fontWeightStyle?.type === 'mixed' ||
    !(fontFamilyStyle?.type === 'shared' && fonts.get(fontFamilyStyle.value)?.weights.includes(700))
  const isBold = fontWeightStyle?.type === 'shared' && fontWeightStyle.value === 700

  const hasSelectedShapes = () => editor.getSelectedShapes().length > 0

  const mustGoBackToSelectToolFirst = () => {
    if (!editor.isIn('select')) {
      editor.complete()
      editor.setCurrentTool('select')
      return false
    }

    return false
  }

  // A textBox prose editor handles its own mark shortcuts, so these defer to it when one is active.
  const actions: Array<Action> = [
    {
      id: 'bold',
      kbd: ['ctrl+b', 'command+b'],
      onSelect() {
        if (getActiveProseView()) return
        if (!hasSelectedShapes()) return
        if (mustGoBackToSelectToolFirst()) return
        if (isBoldDisabled) return

        const value = isBold ? 400 : 700
        editor.setStyleForSelectedShapes(FontWeightStyle, value)
      },
    },
    {
      id: 'italic',
      kbd: ['ctrl+i', 'command+i'],
      onSelect() {
        if (getActiveProseView()) return
        if (!hasSelectedShapes()) return
        if (mustGoBackToSelectToolFirst()) return
        if (!italicStyle || italicStyle.type === 'mixed') return

        editor.setStyleForSelectedShapes(ItalicStyle, !italicStyle.value)
      },
    },
    {
      id: 'underline',
      kbd: ['ctrl+u', 'command+u'],
      onSelect() {
        if (getActiveProseView()) return
        if (!hasSelectedShapes()) return
        if (mustGoBackToSelectToolFirst()) return
        if (!underlineStyle || underlineStyle.type === 'mixed') return

        editor.setStyleForSelectedShapes(UnderlineStyle, !underlineStyle.value)
      },
    },
  ]

  return actions
}
